<script setup lang="ts">
import { computed } from 'vue'
import { resolveAssetUrl } from '~/composables/useAssetUrl'

type AttachmentItem = {
  url: string
  name: string
}

const props = defineProps<{
  attachments?: unknown
  title?: string
}>()
const sectionTitle = computed(() => props.title || '附件下载')

const getFileNameFromUrl = (url: string): string => {
  const cleanUrl = String(url || '').split('?')[0].split('#')[0]
  const fileName = cleanUrl.split('/').pop() || '附件'
  try {
    return decodeURIComponent(fileName)
  } catch {
    return fileName
  }
}

const parseAttachments = (value: unknown): AttachmentItem[] => {
  let raw: unknown[] = []
  if (Array.isArray(value)) {
    raw = value
  } else if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value)
      raw = Array.isArray(parsed) ? parsed : []
    } catch {
      raw = []
    }
  }

  return raw
    .map((item): AttachmentItem | null => {
      if (typeof item === 'string') {
        const url = item.trim()
        if (!url) {
          return null
        }
        return { url, name: getFileNameFromUrl(url) }
      }

      if (!item || typeof item !== 'object') {
        return null
      }

      const record = item as Record<string, unknown>
      const url = String(record.url || record.fileUrl || record.path || record.src || record.file || '').trim()
      if (!url) {
        return null
      }
      const name = String(record.name || record.fileName || record.title || '').trim() || getFileNameFromUrl(url)
      return { url, name }
    })
    .filter((item): item is AttachmentItem => Boolean(item))
}

const files = computed(() => parseAttachments(props.attachments))
</script>

<template>
  <section v-if="files.length" class="attachment-downloads">
    <h3>
      <i class="fa-solid fa-link" aria-hidden="true" />
      {{ sectionTitle }}
    </h3>
    <div class="attachment-list">
      <a
        v-for="file in files"
        :key="`${file.url}-${file.name}`"
        :href="resolveAssetUrl(file.url)"
        target="_blank"
        rel="noopener noreferrer"
        class="attachment-link"
      >
        <i class="fa-regular fa-file-lines" aria-hidden="true" />
        <span>{{ file.name }}</span>
      </a>
    </div>
  </section>
</template>

<style scoped>
.attachment-downloads {
  margin-top: 14px;
  padding: 12px;
  border: 0;
  border-radius: 12px;
  background: var(--bg);
}

.attachment-downloads h3 {
  margin: 0 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
}

.attachment-list {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.attachment-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text);
  text-decoration: none;
  font-size: 14px;
  width: 100%;
  min-width: 0;
}

.attachment-link i {
  font-size: 15px;
  color: var(--text-soft);
  flex: 0 0 auto;
}

.attachment-link span {
  flex: 1 1 auto;
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
